import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { initConsentDefaults, initAnalytics, trackPageView } from "./analytics";
import { ConsentBanner } from "./components/ConsentBanner";
import "@/App.css";
import { setLenis, getLenis } from "./lib/scroll";
import { LanguageProvider } from "./i18n/LanguageContext";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Pillars } from "./components/Pillars";
import { ParceirosInstitucionais } from "./components/ParceirosInstitucionais";
import { CTAReserva } from "./components/CTAReserva";
import { AudioExperience } from "./components/AudioExperience";
import { CulturaNoticias } from "./components/CulturaNoticias";
import { Patrocinadores } from "./components/Patrocinadores";
import { Localizacao } from "./components/Localizacao";
import { Artistas } from "./components/Artistas";
import { Footer } from "./components/Footer";
import ArtistPage from "./pages/ArtistPage";
import ExperienciaPage from "./pages/ExperienciaPage";
import PatrocinadoresPage from "./pages/PatrocinadoresPage";
import LocalPage from "./pages/LocalPage";
import EquipaPage from "./pages/EquipaPage";
import MissaoPage from "./pages/MissaoPage";
import ContactoPage from "./pages/ContactoPage";
import NoticiasPage from "./pages/NoticiasPage";
import PrivacidadePage from "./pages/PrivacidadePage";
import TermosPage from "./pages/TermosPage";
import ConfirmacaoPage from "./pages/ConfirmacaoPage";
import AdminPage from "./pages/AdminPage";
import RecursosPage from "./pages/RecursosPage";
import BilhetesPage from "./pages/BilhetesPage";
import VantagensSocioPage from "./pages/VantagensSocioPage";
import CartaoPage from "./pages/CartaoPage";
import CartaoMembroPage from "./pages/CartaoMembroPage";
import AssociacaoPage from "./pages/AssociacaoPage";
import { ARTISTS } from "./data/artistas";
import { useLanguage } from "./i18n/LanguageContext";
import { useSEO, eventLd, organizationLd, websiteLd, breadcrumbLd } from "./components/SEO";
import { SHOW_INSTITUTIONAL } from "./config";
import { antesDoFim1aEdicao } from "./data/datas";
import { ConvidadoHonra } from "./components/ConvidadoHonra";
import { InfluencerOficial } from "./components/InfluencerOficial";

const Landing = () => {
    const { t } = useLanguage();
    useSEO({
        title: t.seo.home.title,
        description: t.seo.home.desc,
        keywords: t.seo.keywords,
        jsonLd: [organizationLd, websiteLd, eventLd(ARTISTS), breadcrumbLd([{ name: t.bc.home, path: "/" }])],
    });
    return (
    <div className="bg-[#070503] text-[#efe6d6] min-h-screen">
        <h1 className="sr-only">{t.seo.home.title}</h1>
        <Navbar />
        <main>
            <Hero />
            <Pillars />
            <ConvidadoHonra />
            <Patrocinadores />
            <Artistas />
            <InfluencerOficial />
            <Localizacao />
            {SHOW_INSTITUTIONAL && <ParceirosInstitucionais />}
            <CTAReserva />
            <CulturaNoticias />
        </main>
        <Footer />
        <AudioExperience />
    </div>
    );
};

const ScrollToTop = () => {
    const { pathname, hash } = useLocation();    useEffect(() => {
        const lenis = getLenis();
        if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (hash) {
            const target = hash;
            const timer = setTimeout(() => {
                const el = document.querySelector(target);
                if (!el) return;
                const l = getLenis();
                if (l) l.scrollTo(el, { immediate: true, force: true });
                else el.scrollIntoView();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [pathname, hash]);
    return null;
};

const AnalyticsTracker = () => {
    const { pathname } = useLocation();
    useEffect(() => { initConsentDefaults(); initAnalytics(); }, []);
    useEffect(() => { trackPageView(); }, [pathname]);
    return null;
};

const LangUrlSync = () => {
    const { lang } = useLanguage();
    const { pathname } = useLocation();
    useEffect(() => {
        const url = new URL(window.location.href);
        if (url.searchParams.get("lang") !== lang) {
            url.searchParams.set("lang", lang);
            window.history.replaceState(null, "", url);
        }
    }, [lang, pathname]);
    return null;
};

function App() {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        setLenis(lenis);
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            setLenis(null);
        };
    }, []);

    return (
        <LanguageProvider>
        <BrowserRouter>
            <ScrollToTop />
            <LangUrlSync />
            <AnalyticsTracker />
            <ConsentBanner />
            <Routes>
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/index" element={<Navigate to="/" replace />} />
                <Route path="/" element={<Landing />} />
                <Route path="/artistas" element={<Navigate to="/#artistas" replace />} />
                <Route path="/artistas/:slug" element={<ArtistPage />} />
                <Route path="/cartao/:slug" element={<CartaoPage />} />
                <Route path="/cartao-membro/:id" element={<CartaoMembroPage />} />
                <Route path="/associacao" element={<AssociacaoPage />} />
                <Route path="/experiencia" element={<ExperienciaPage />} />
                <Route path="/patrocinadores" element={<PatrocinadoresPage />} />
                <Route path="/local" element={<LocalPage />} />
                <Route path="/equipa" element={antesDoFim1aEdicao() ? <EquipaPage /> : <Navigate to="/experiencia" replace />} />
                <Route path="/missao" element={<MissaoPage />} />
                <Route path="/contacto" element={<ContactoPage />} />
                <Route path="/galeria" element={<Navigate to="/noticias" replace />} />
                <Route path="/noticias" element={<NoticiasPage />} />
                <Route path="/privacidade" element={<PrivacidadePage />} />
                <Route path="/termos" element={<TermosPage />} />
                <Route path="/confirmar" element={<ConfirmacaoPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/recursos" element={<RecursosPage />} />
                <Route path="/bilhetes" element={<BilhetesPage />} />
                <Route path="/socios/vantagens" element={<VantagensSocioPage />} />
            </Routes>
        </BrowserRouter>
        </LanguageProvider>
    );
}

export default App;
